import { createPortal } from 'react-dom'
import { SvgFunnel, SvgSearchIcon, SvgClearCross } from '../assets/images'
import { debounce, isEmpty, noop } from 'lodash'
import {
  NAMESPACE,
  SearchPositionPropsState,
  PositionType,
  SearchBarProps,
} from '../utils'
import { useEffect, useRef, useState } from 'react'
import Checkbox from './Checkbox'
import CustomButton from './CustomButton'

const SearchPopup = ({
  top,
  left,
  setShowFilterOptions,
  setIsClicked,
  filterOptions,
  childRef,
  onFilter = noop,
}: SearchPositionPropsState) => {
  const container = document.getElementById('searchbar-parent')
  const [selectedOptions, setSelectedOptions] = useState<string[]>([])
  const handleCheckboxClick = (val: string) => {
    setSelectedOptions((prev) => {
      if (prev.includes(val)) {
        return prev.filter((option) => option !== val)
      } else {
        return [...prev, val]
      }
    })
  }
  const handleButtonClick = () => {
    onFilter && onFilter(selectedOptions)
    setShowFilterOptions(false)
    setIsClicked(false)
  }
  return container
    ? createPortal(
        <div
          className="absolute z-[999] child-searchbar-popup"
          ref={childRef}
          style={{ top: top + 25, left: left - 200 }}
        >
          <div className="p-4 bg-accent-0 rounded shadow-lg min-w-[12.8rem] max-w-[15rem]">
            <h2 className="text-xs font-medium mb-4">{NAMESPACE.FILTER_BY}</h2>
            <ul className="space-y-3  max-h-[8rem] overflow-y-auto hide-scrollbar">
              {filterOptions.map((option) => (
                <li key={option.value} className="flex items-center">
                  <Checkbox
                    dataQaId="searchbar-checkbox"
                    label={''}
                    id={option.value}
                    name={''}
                    onClick={() => handleCheckboxClick(option.value)}
                  />
                  <label
                    htmlFor={option.value}
                    className="text-xs font-normal text-accent-700"
                  >
                    {option.label}
                  </label>
                </li>
              ))}
            </ul>
            <div className="mt-2 flex items-center justify-end">
              <CustomButton
                dataQaId="searchbar-button"
                color="primary"
                size="small"
                isFilled={true}
                widthClass="w-20"
                onClick={handleButtonClick}
              >
                <span className="flex items-center justify-center">
                  {NAMESPACE.APPLY}
                </span>
              </CustomButton>
            </div>
          </div>
        </div>,
        container
      )
    : null
}

const SearchBar: React.FC<SearchBarProps> = ({
  width = 'w-[18rem]',
  height = 'h-10',
  onKeyDown,
  onSearch,
  onFilter = noop,
  showFunnel = true,
  showCrossIcon = false,
  dataQaId = '',
  filterOptions = [],
  placeholder = 'Search',
  placeholderFontSize = 'text-sm',
  searchLength = 3,
  autoFocus,
  tabIndex,
  inputRef,
}: SearchBarProps) => {
  const [isClicked, setIsClicked] = useState<boolean>(false)
  const [inputValue, setInputValue] = useState<string>('')
  const childRef = useRef<HTMLDivElement | null>(null)
  const popupRef = useRef<HTMLDivElement | null>(null)
  const [showFilterOptions, setShowFilterOptions] = useState<boolean>(false)
  const [position, setPosition] = useState<PositionType | null>(null)
  const debouncedSearch = debounce((value: string) => {
    if (value.length >= searchLength || value.length === 0) {
      onSearch(value)
    }
  }, 300)

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setInputValue(value)
    debouncedSearch(value)
  }
  const handleFunnelClick = (e: React.MouseEvent<HTMLDivElement>) => {
    setIsClicked(!isClicked)
    setShowFilterOptions(!showFilterOptions)
    setPosition({
      top: e.clientY + window.scrollY,
      left: e.clientX + window.scrollX,
    })
  }
  const handleClearInput = () => {
    setInputValue('')
    onSearch('')
  }
  const handleClickOutside = (event: MouseEvent) => {
    const popupNode = popupRef.current
    const childNode = childRef.current
    if (
      popupNode &&
      childNode &&
      !(event.target as Element).closest('.child-searchbar-popup') &&
      !(event.target as Element).closest('.funnel-button')
    ) {
      setIsClicked(false)
      setShowFilterOptions(false)
    }
  }
  const handleScroll = () => {
    setIsClicked(false)
    setShowFilterOptions(false)
  }

  useEffect(() => {
    const popupNode = popupRef.current
    document.addEventListener('mousedown', handleClickOutside)
    if (popupNode) popupNode.addEventListener('scroll', handleScroll)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      if (popupNode) popupNode.removeEventListener('scroll', handleScroll)
    }
  }, [])

  return (
    <div
      style={{ height: height }}
      className={`font-primary border rounded-[0.25rem] group flex bg-accent-0 justify-between items-center ${height} ${width} ${placeholderFontSize}  hover:border-secondary-500`}
    >
      <div className="flex items-center gap-2 w-full">
        <div className="ml-3 w-[0.8rem] h-[0.8rem]">
          <SvgSearchIcon
            className="svg-fill-red-300 group-hover:svg-fill-red-300"
            height={'0.75rem'}
            width={'0.75rem'}
          />
        </div>
        <input
          value={inputValue}
          ref={inputRef}
          data-qa-id={dataQaId || ''}
          onKeyDown={onKeyDown || noop}
          type="text"
          placeholder={placeholder}
          className={`${width} border-none focus:outline-none w-full  bg-accent-0 text-black placeholder-accent-600 font-light text-xs group-hover:placeholder-secondary-500`}
          onChange={handleSearch}
          autoFocus={autoFocus}
          tabIndex={tabIndex}
        />
        {showCrossIcon && inputValue && (
          <div
            className="w-11 h-5 flex items-center justify-center cursor-pointer"
            onClick={handleClearInput}
          >
            <SvgClearCross width="10" height="10" />
          </div>
        )}
      </div>
      {showFunnel && (
        <>
          <div
            className={`mr-2 hover:cursor-pointer funnel-button ${
              isClicked ? 'bg-secondary-50 rounded' : ''
            }`}
            ref={popupRef}
            onClick={handleFunnelClick}
          >
            <SvgFunnel
              className={
                isClicked
                  ? 'svg-path-fill-secondary-500'
                  : 'svg-path-fill-accent-300'
              }
            />
          </div>
          {showFilterOptions && !isEmpty(position) ? (
            <SearchPopup
              onFilter={onFilter}
              childRef={childRef}
              setIsClicked={setIsClicked}
              top={position.top}
              left={position.left}
              setShowFilterOptions={setShowFilterOptions}
              filterOptions={filterOptions}
            />
          ) : null}
        </>
      )}
    </div>
  )
}
export default SearchBar
